<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { Separator } from '@/components/ui/separator';
    import { columns } from '@/tables/columns/payrolls';
    import { computed } from 'vue';
    import { handleQueryRetry } from '@/utils/helpers';
    import { Head } from '@inertiajs/vue3';
    // Removed custom button and export override in favor of reusing the shared export component
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreatePayroll from '@/components/salary/CreatePayroll.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PayrollStatusSelect from '@/components/salary/PayrollStatusSelect.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, BreadcrumbItem } from '@/types';
    import { ref, watch } from 'vue';
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
    const selectedStatus = ref('open');
    const { hasPermission } = usePermissions();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'dashboards.payroll',
        },
        {
            title: 'payrolls.payroll_header',
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.PAYROLLS, selectedCompanyId, selectedStatus],
        queryFn: getPayrolls,
        retry: handleQueryRetry,
    });

    async function getPayrolls() {
        const { data } = await axios.get(
            route('v2.salary.payrolls.table', {
                companyId: selectedCompanyId.value,
                filter: selectedStatus.value,
            }),
        );
        return data.data;
    }

</script>

<template>
    <Head :title="$t('dashboards.employee.payroll_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        require-selected-company
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.PAYROLLS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :enable-export="true"
                :default-column-visibility="{
                    id: false,
                }"
                :default-sorting="[
                    {
                        id: 'year',
                        desc: true,
                    },
                    {
                        id: 'month',
                        desc: true,
                    },
                ]"
            >
                <template #title>
                    <CardTitle>
                        {{ $t('payrolls.payroll_header') }}
                    </CardTitle>
                </template>
                <template #actions>
                    <DataTableToolbarActions name="payrolls">
                        <div class="w-[91px]">
                            <PayrollStatusSelect
                                v-model="selectedStatus"
                            />
                        </div>
                        <Separator
                            orientation="vertical"
                            class="h-2.5!"
                        />
                        <CreatePayroll
                            v-if="hasPermission(Permission.SALARY_PAYROLLS_CREATE)"
                            :selected-company-id="selectedCompanyId"
                        />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
